// Customers (D-163): every customer the person may see (a telecaller, those assigned to her), found by name or number
// and filtered by stage, label, follow-up, contact, owner and source, with ready-made views and the counts per stage.
// Owners, admins and managers hand customers over many at a time. The filters live in the address, so a view can be
// shared and comes back on Back.
import { type FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  CUSTOMER_VIEWS,
  type CustomerQuery,
  customerQueryFromParams,
  customerQueryToParams,
  type CustomerRow,
  CUSTOMERS_PAGE,
  customersCsv,
  EXPORT_LIMIT,
  viewOf,
} from "../lib/crm";
import { fmtAgo, fmtNumber, fmtPhone, isManagerRole, localDate } from "../lib/format";
import { useMember } from "../app/session";
import { Due, KindBadge, LabelBadge, StageTag } from "../app/crm";
import {
  Card,
  Empty,
  errorMessage,
  ErrorNote,
  Field,
  Loading,
  Modal,
  Page,
  useAction,
  useLoad,
  useToast,
} from "../app/ui";

export function Leads() {
  const { api, member, tz } = useMember();
  const [params, setParams] = useSearchParams();
  const [adding, setAdding] = useState(false);
  const query = customerQueryFromParams(params);
  const key = params.toString();
  const { data, error, loading, reload } = useLoad(() => api.customerList(query), [api, key], true);
  const members = useLoad(() => api.members(), [api]);
  const manager = isManagerRole(member.role);
  const [search, setSearch] = useState(query.q ?? "");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const view = viewOf(query);
  const toast = useToast();
  const [exporting, setExporting] = useState(false);

  const go = (next: CustomerQuery) => {
    setSelected(new Set());
    setParams(customerQueryToParams(next));
  };
  const set = (k: keyof CustomerQuery, v: string) => go({ ...query, [k]: v, page: 0 });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const from = query.page * CUSTOMERS_PAGE;
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const openStages = (data?.stages ?? []).filter((s) => s.kind === "open");

  // The customers the filters show, as a spreadsheet: owners, admins and managers, up to EXPORT_LIMIT at a time.
  const download = async () => {
    setExporting(true);
    try {
      const all: CustomerRow[] = [];
      for (let page = 0; all.length < Math.min(total, EXPORT_LIMIT); page++) {
        const res = await api.customerList({ ...query, page });
        all.push(...res.rows);
        if (res.rows.length < CUSTOMERS_PAGE) break;
      }
      const url = URL.createObjectURL(new Blob([customersCsv(all.slice(0, EXPORT_LIMIT), tz)], { type: "text/csv" }));
      const a = Object.assign(document.createElement("a"), {
        href: url,
        download: `customers-${localDate(new Date(), tz)}.csv`,
      });
      a.click();
      URL.revokeObjectURL(url);
      toast(
        total > EXPORT_LIMIT
          ? `The first ${fmtNumber(EXPORT_LIMIT)} of ${fmtNumber(total)} customers were downloaded.`
          : `${fmtNumber(all.length)} customers downloaded`,
      );
    } catch (e) {
      toast(errorMessage(e), true);
    } finally {
      setExporting(false);
    }
  };
  const closedStages = (data?.stages ?? []).filter((s) => s.kind !== "open");

  return (
    <Page
      title={manager ? "Customers" : "My customers"}
      sub={data ? `${fmtNumber(total)} ${total === 1 ? "customer" : "customers"}` : undefined}
      actions={
        <>
          {manager && (
            <button disabled={exporting || !total} onClick={() => void download()}>
              {exporting ? "Preparing…" : "Download CSV"}
            </button>
          )}
          <button className="primary" onClick={() => setAdding(true)}>Add customer</button>
        </>
      }
    >
      <div className="pipeline-strip" role="group" aria-label="Stages">
        <button
          className={`stage-chip${!query.stage && !query.status ? " on" : ""}`}
          onClick={() => go({ ...query, stage: "", status: "", page: 0 })}
        >
          <span>All</span>
        </button>
        {openStages.map((s) => (
          <button
            key={s.id}
            className={`stage-chip${query.stage === s.id ? " on" : ""}`}
            onClick={() => go({ ...query, stage: query.stage === s.id ? "" : s.id, status: "", page: 0 })}
          >
            <span>{s.name}</span>
            <strong>{fmtNumber(s.count)}</strong>
          </button>
        ))}
        {closedStages.map((s) => (
          <button
            key={s.id}
            className={`stage-chip closed${s.kind === "won" ? " won" : ""}${query.stage === s.id ? " on" : ""}`}
            onClick={() => go({ ...query, stage: query.stage === s.id ? "" : s.id, status: "", page: 0 })}
          >
            <span>{s.name}</span>
            <strong>{fmtNumber(s.count)}</strong>
          </button>
        ))}
      </div>

      <div className="chips views" role="group" aria-label="Views">
        {CUSTOMER_VIEWS.filter((v) => manager || !v.managers).map((v) => (
          <button
            key={v.key}
            className={`chip${view === v.key ? " on" : ""}`}
            aria-pressed={view === v.key}
            onClick={() =>
              go(view === v.key ? { page: 0, sort: query.sort } : { ...v.query, sort: query.sort, page: 0 })}
          >
            {v.label}
          </button>
        ))}
      </div>

      <form
        className="filters"
        onSubmit={(e) => {
          e.preventDefault();
          set("q", search.trim());
        }}
      >
        <input
          type="search"
          placeholder="Name or number"
          aria-label="Search customers"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          aria-label="Status"
          value={query.status ?? ""}
          onChange={(e) => go({ ...query, status: e.target.value as CustomerQuery["status"], stage: "", page: 0 })}
        >
          <option value="">Open and closed</option>
          <option value="open">Open</option>
          <option value="won">Won</option>
          <option value="lost">Lost</option>
          <option value="closed">Closed</option>
        </select>
        <select aria-label="Label" value={query.label ?? ""} onChange={(e) => set("label", e.target.value)}>
          <option value="">Any label</option>
          <option value="hot">Hot</option>
          <option value="warm">Warm</option>
          <option value="cold">Cold</option>
          <option value="none">No label</option>
        </select>
        <select aria-label="Follow-up" value={query.follow_up ?? ""} onChange={(e) => set("follow_up", e.target.value)}>
          <option value="">Any follow-up</option>
          <option value="overdue">Overdue</option>
          <option value="today">Due today</option>
          <option value="upcoming">Later</option>
          <option value="none">None planned</option>
        </select>
        <select aria-label="Last contact" value={query.contact ?? ""} onChange={(e) => set("contact", e.target.value)}>
          <option value="">Any contact</option>
          <option value="never">Never called</option>
          <option value="stale">Not in 7 days</option>
          <option value="week">In the last 7 days</option>
        </select>
        <select aria-label="Added" value={query.added ?? ""} onChange={(e) => set("added", e.target.value)}>
          <option value="">Added any time</option>
          <option value="today">Added today</option>
          <option value="week">In the last 7 days</option>
          <option value="month">In the last 30 days</option>
        </select>
        {manager && (
          <select aria-label="Owner" value={query.owner ?? ""} onChange={(e) => set("owner", e.target.value)}>
            <option value="">Everyone's</option>
            <option value="me">Mine</option>
            {(data?.owners ?? []).map((o) => (
              <option key={o.id ?? "none"} value={o.id ?? "none"}>
                {o.id ? o.name : "Nobody's"} ({fmtNumber(o.count)})
              </option>
            ))}
          </select>
        )}
        <select aria-label="Source" value={query.source ?? ""} onChange={(e) => set("source", e.target.value)}>
          <option value="">Any source</option>
          {(data?.sources ?? []).map((s) => (
            <option key={s.id ?? "none"} value={s.id ?? "none"}>
              {s.id ? s.name : "No source"} ({fmtNumber(s.count)})
            </option>
          ))}
        </select>
        <select aria-label="AI reading" value={query.ai ?? ""} onChange={(e) => set("ai", e.target.value)}>
          <option value="">Any AI reading</option>
          <option value="hot">AI: hot</option>
          <option value="warm">AI: warm</option>
          <option value="cold">AI: cold</option>
        </select>
        <select aria-label="Sort" value={query.sort ?? ""} onChange={(e) => set("sort", e.target.value)}>
          <option value="">Next follow-up first</option>
          <option value="recent">Last contact first</option>
          <option value="newest">Newest first</option>
          <option value="name">By name</option>
        </select>
        <button>Search</button>
        {key && (
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setSearch("");
              go({ page: 0 });
            }}
          >
            Clear
          </button>
        )}
      </form>

      {manager && selected.size > 0 && (
        <HandOver
          ids={[...selected]}
          onDone={async () => {
            setSelected(new Set());
            await reload();
          }}
          onClear={() => setSelected(new Set())}
        />
      )}

      <ErrorNote error={error} />
      <Card flush>
        {loading && !data && (
          <div style={{ padding: 16 }}>
            <Loading />
          </div>
        )}
        {data && rows.length === 0 && <Empty>No customers match.</Empty>}
        {rows.length > 0 && (
          <div className="table-wrap">
            <table className="customers">
              <thead>
                <tr>
                  {manager && (
                    <th className="pick">
                      <input
                        type="checkbox"
                        aria-label="Choose every customer on this page"
                        checked={allOnPage}
                        onChange={() =>
                          setSelected((s) => {
                            const next = new Set(s);
                            for (const r of rows) {
                              if (allOnPage) next.delete(r.id);
                              else next.add(r.id);
                            }
                            return next;
                          })}
                      />
                    </th>
                  )}
                  <th>Customer</th>
                  <th>Stage</th>
                  <th>Next follow-up</th>
                  <th>Last contact</th>
                  {manager && <th>Owner</th>}
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <CustomerLine
                    key={r.id}
                    row={r}
                    tz={tz}
                    manager={manager}
                    me={member.member_id}
                    picked={selected.has(r.id)}
                    onPick={() =>
                      setSelected((s) => {
                        const next = new Set(s);
                        if (next.has(r.id)) next.delete(r.id);
                        else next.add(r.id);
                        return next;
                      })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {total > CUSTOMERS_PAGE && (
        <div className="row between pager">
          <span className="faint">
            {fmtNumber(from + 1)}–{fmtNumber(Math.min(from + CUSTOMERS_PAGE, total))} of {fmtNumber(total)}
          </span>
          <span className="row">
            <button disabled={query.page === 0} onClick={() => go({ ...query, page: query.page - 1 })}>Previous</button>
            <button disabled={from + CUSTOMERS_PAGE >= total} onClick={() => go({ ...query, page: query.page + 1 })}>
              Next
            </button>
          </span>
        </div>
      )}
      {adding && <AddCustomer members={members.data ?? []} onClose={() => setAdding(false)} />}
    </Page>
  );
}

function CustomerLine(props: {
  row: CustomerRow;
  tz: string;
  manager: boolean;
  me: string;
  picked: boolean;
  onPick: () => void;
}) {
  const r = props.row;
  const next = r.next_follow_up;
  return (
    <tr className={props.picked ? "picked" : undefined}>
      {props.manager && (
        <td className="pick">
          <input type="checkbox" aria-label={`Choose ${r.name}`} checked={props.picked} onChange={props.onPick} />
        </td>
      )}
      <td className="c-name">
        <div className="row">
          <Link className="primary" to={`/leads/${r.id}`}>{r.has_name ? r.name : fmtPhone(r.phone)}</Link>
          <LabelBadge label={r.label} />
        </div>
        <div className="faint">
          {r.has_name && fmtPhone(r.phone)}
          {r.context && <>{r.has_name ? " · " : ""}{r.context}</>}
        </div>
      </td>
      <td className="c-stage">
        <StageTag stage={r.stage} />
      </td>
      <td className="c-next">
        {next
          ? (
            <>
              <div className="row">
                <KindBadge type={next.type} />
                <Due dueAt={next.due_at} hasTime={next.due_has_time} tz={props.tz} />
              </div>
              {props.manager && next.member && next.member.id !== r.owner?.id && (
                <div className="faint">{next.member.name}</div>
              )}
              {r.open_follow_ups > 1 && <div className="faint">and {r.open_follow_ups - 1} more</div>}
            </>
          )
          : r.status === "open"
          ? <span className="faint">None planned</span>
          : <span className="faint">–</span>}
      </td>
      <td className="nowrap c-last">
        {r.last_contact_at
          ? (
            <>
              <div>{fmtAgo(r.last_contact_at, props.tz)}</div>
              <div className="faint">{r.calls} {r.calls === 1 ? "call" : "calls"}</div>
            </>
          )
          : (
            <>
              <div className="faint">Never called</div>
              {r.enquired_at && <div className="faint">Enquired {fmtAgo(r.enquired_at, props.tz)}</div>}
            </>
          )}
      </td>
      {props.manager && (
        <td className="c-owner">{r.owner ? r.owner.name : <span className="badge danger">Nobody</span>}</td>
      )}
      <td className="c-source">
        <div>{r.source?.name ?? originName(r.origin)}</div>
        {r.campaign && <div className="faint">{r.campaign}</div>}
      </td>
    </tr>
  );
}

function originName(origin: string): string {
  return { call_log: "Calls", import: "Imported", manual: "Added by hand", integration: "Integration" }[origin] ??
    origin;
}

/** Hand the chosen customers to someone, or to nobody; their open follow-ups go along (LEAD-19). */
function HandOver(props: { ids: string[]; onDone: () => Promise<void>; onClear: () => void }) {
  const { api } = useMember();
  const toast = useToast();
  const members = useLoad(() => api.members(), [api]);
  const [to, setTo] = useState("");
  const { busy, run } = useAction();
  const team = (members.data ?? []).filter((m) => m.status === "active");
  const name = team.find((m) => m.id === to)?.display_name ?? "";
  const handOver = async () => {
    const ok = await run(async () => {
      const n = await api.assignCustomers(props.ids, to === "none" ? null : to);
      const what = `${n} ${n === 1 ? "customer" : "customers"}`;
      toast(to === "none" ? `${what} left with nobody` : `${what} handed to ${name}`);
    });
    if (ok) {
      setTo("");
      await props.onDone();
    }
  };
  return (
    <div className="bulk-bar" role="region" aria-label="Chosen customers">
      <strong>{props.ids.length} chosen</strong>
      <select aria-label="Hand to" value={to} onChange={(e) => setTo(e.target.value)}>
        <option value="">Hand to…</option>
        {team.map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
        <option value="none">Nobody</option>
      </select>
      <button className="primary small" disabled={busy || !to} onClick={() => void handOver()}>Hand over</button>
      {to && to !== "none" && <span className="faint">Their open follow-ups go to {name} too.</span>}
      {to === "none" && <span className="faint">Their open follow-ups come to you.</span>}
      <button className="small ghost" onClick={props.onClear}>Clear</button>
    </div>
  );
}

function AddCustomer(
  { members, onClose }: { members: { id: string; display_name: string; status: string }[]; onClose: () => void },
) {
  const { api, member } = useMember();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [owner, setOwner] = useState(member.role === "caller" ? member.member_id : "");
  const { busy, run } = useAction();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    let id = "";
    if (
      await run(async () => {
        id = await api.addLead({ phone, name, ownerMemberId: owner || undefined });
      }, "Customer saved")
    ) {
      onClose();
      navigate(`/leads/${id}`);
    }
  };

  return (
    <Modal title="Add a customer" onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Mobile number" hint="A number already with a customer opens that customer instead.">
          <input
            required
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="98765 43210"
            autoFocus
          />
        </Field>
        <Field label="Name (optional)">
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        {isManagerRole(member.role) && (
          <Field label="Whose customer">
            <select value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">Nobody's yet</option>
              {members.filter((m) => m.status === "active").map((m) => (
                <option key={m.id} value={m.id}>{m.display_name}</option>
              ))}
            </select>
          </Field>
        )}
        <div className="actions">
          <button type="button" onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy || !phone.trim()}>Save</button>
        </div>
      </form>
    </Modal>
  );
}
